import React, { useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Grid,
  Typography,
  Link,
  Divider,
  IconButton,
  CircularProgress
} from '@mui/material';
import {
  Facebook as FacebookIcon,
  Twitter as TwitterIcon,
  Instagram as InstagramIcon,
  YouTube as YouTubeIcon,
  WhatsApp as WhatsAppIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationOnIcon
} from '@mui/icons-material';
import { useSettings } from '../../contexts/SettingsContext';
import { useStoreSettings } from '../../contexts/StoreSettingsContext';
import FooterLinks from './FooterLinks';
import { createDefaultPages } from '../../services/pageService';

const StoreFooter: React.FC = () => {
  // const theme = useTheme();
  const { settings } = useSettings();
  const { settings: storeSettings, loading: storeLoading } = useStoreSettings();
  const currentYear = new Date().getFullYear();

  // إنشاء الصفحات الافتراضية عند تحميل المكون
  useEffect(() => {
    const initializeDefaultPages = async () => {
      try {
        await createDefaultPages();
      } catch (error) {
        console.error('Error initializing default pages:', error);
      }
    };

    initializeDefaultPages();
  }, []);

  // استخراج بيانات المتجر من الإعدادات مع معالجة حالة التحميل
  const storeName = storeSettings?.general?.storeName || settings.companyName || 'اليكترونيات السواح';
  const storeEmail = storeSettings?.general?.storeEmail || 'info@alsawah-electronics.com';
  const storePhone = storeSettings?.general?.storePhone || '0123456789';
  const storeAddress = storeSettings?.general?.storeAddress || 'العنوان، المدينة، البلد';
  const socialLinks = storeSettings?.general?.socialLinks || {};

  // إذا كانت البيانات لا تزال تحمل، عرض Loading
  if (storeLoading) {
    return (
      <Box
        component="footer"
        className="store-footer"
        sx={{
          bgcolor: 'primary.main',
          color: 'white',
          py: 6,
          mt: 'auto',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 200
        }}
      >
        <CircularProgress color="inherit" />
      </Box>
    );
  }

  return (
    <Box
      component="footer"
      className="store-footer"
      sx={{
        bgcolor: 'primary.main',
        color: 'white',
        py: 6,
        mt: 'auto'
      }}
    >
      <Container maxWidth={false} sx={{ px: { xs: 1, sm: 2, md: 3 } }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={4}>
            <Typography variant="h6" gutterBottom>
              {storeName}
            </Typography>
            {settings.companyLogo && (
              <Box
                component="img"
                src={settings.companyLogo}
                alt={settings.companyName}
                sx={{ height: 60, mb: 2 }}
              />
            )}
            <Typography variant="body2" paragraph>
              متخصصون في بيع وصيانة جميع أنواع الأجهزة الإلكترونية والكهربائية بأفضل الأسعار وأعلى جودة.
            </Typography>
            <Box sx={{ mt: 2 }}>
              {socialLinks.facebook && (
                <IconButton
                  color="inherit"
                  aria-label="Facebook"
                  component="a"
                  href={socialLinks.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FacebookIcon />
                </IconButton>
              )}
              {socialLinks.twitter && (
                <IconButton
                  color="inherit"
                  aria-label="Twitter"
                  component="a"
                  href={socialLinks.twitter}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <TwitterIcon />
                </IconButton>
              )}
              {socialLinks.instagram && (
                <IconButton
                  color="inherit"
                  aria-label="Instagram"
                  component="a"
                  href={socialLinks.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <InstagramIcon />
                </IconButton>
              )}
              {socialLinks.youtube && (
                <IconButton
                  color="inherit"
                  aria-label="YouTube"
                  component="a"
                  href={socialLinks.youtube}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <YouTubeIcon />
                </IconButton>
              )}
              {socialLinks.whatsapp && (
                <IconButton
                  color="inherit"
                  aria-label="WhatsApp"
                  component="a"
                  href={socialLinks.whatsapp}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <WhatsAppIcon />
                </IconButton>
              )}
              {/* إذا لم تكن هناك روابط اجتماعية، عرض الأيقونات الافتراضية */}
              {!socialLinks.facebook && !socialLinks.twitter && !socialLinks.instagram &&
               !socialLinks.youtube && !socialLinks.whatsapp && (
                <>
                  <IconButton color="inherit" aria-label="Facebook">
                    <FacebookIcon />
                  </IconButton>
                  <IconButton color="inherit" aria-label="Twitter">
                    <TwitterIcon />
                  </IconButton>
                  <IconButton color="inherit" aria-label="Instagram">
                    <InstagramIcon />
                  </IconButton>
                  <IconButton color="inherit" aria-label="YouTube">
                    <YouTubeIcon />
                  </IconButton>
                  <IconButton color="inherit" aria-label="WhatsApp">
                    <WhatsAppIcon />
                  </IconButton>
                </>
              )}
            </Box>
          </Grid>

          <Grid item xs={12} md={4}>
            <FooterLinks section="quick_links" title="روابط سريعة" />
          </Grid>

          <Grid item xs={12} md={4}>
            <Typography variant="h6" gutterBottom>
              اتصل بنا
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <LocationOnIcon sx={{ mr: 1 }} />
              <Typography variant="body2">
                {storeAddress}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <PhoneIcon sx={{ mr: 1 }} />
              <Typography variant="body2">
                {storePhone}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <EmailIcon sx={{ mr: 1 }} />
              <Typography variant="body2">
                {storeEmail}
              </Typography>
            </Box>
            <Typography variant="subtitle2" sx={{ mt: 2 }}>
              ساعات العمل
            </Typography>
            {storeSettings?.general?.workingHours ? (
              <>
                <Typography variant="body2">
                  السبت: {storeSettings.general.workingHours.saturday || '9:00 صباحًا - 9:00 مساءً'}
                </Typography>
                <Typography variant="body2">
                  الأحد: {storeSettings.general.workingHours.sunday || '9:00 صباحًا - 9:00 مساءً'}
                </Typography>
                <Typography variant="body2">
                  الإثنين: {storeSettings.general.workingHours.monday || '9:00 صباحًا - 9:00 مساءً'}
                </Typography>
                <Typography variant="body2">
                  الثلاثاء: {storeSettings.general.workingHours.tuesday || '9:00 صباحًا - 9:00 مساءً'}
                </Typography>
                <Typography variant="body2">
                  الأربعاء: {storeSettings.general.workingHours.wednesday || '9:00 صباحًا - 9:00 مساءً'}
                </Typography>
                <Typography variant="body2">
                  الخميس: {storeSettings.general.workingHours.thursday || '9:00 صباحًا - 9:00 مساءً'}
                </Typography>
                <Typography variant="body2">
                  الجمعة: {storeSettings.general.workingHours.friday || '2:00 مساءً - 9:00 مساءً'}
                </Typography>
              </>
            ) : (
              <>
                <Typography variant="body2">
                  السبت - الخميس: 9:00 صباحًا - 9:00 مساءً
                </Typography>
                <Typography variant="body2">
                  الجمعة: 2:00 مساءً - 9:00 مساءً
                </Typography>
              </>
            )}
          </Grid>
        </Grid>

        <Divider sx={{ my: 3, borderColor: 'rgba(255, 255, 255, 0.2)' }} />

        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2" sx={{ mb: { xs: 2, sm: 0 } }}>
            &copy; {currentYear} {storeName}. جميع الحقوق محفوظة.
          </Typography>
          <Typography variant="body2">
            تطوير بواسطة Ahmed Elswah | الإصدار 4.0
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default StoreFooter;
